import { useTheme } from '@/hooks/useTheme';
import { getColorOpacityFromCSSVar, getColorOpacityFromHex } from '@/lib/utils';
import { PluginConfigData } from '@/types/plugin-config';
import { usePluginConfig } from '@tagadapay/plugin-sdk/v2';

export function ThemeSetter() {
  const { primaryColor } = useTheme();
  const { config } = usePluginConfig<PluginConfigData>();
  const fontFamily = config?.branding?.fontFamily || 'inter';
  const textColor = config?.branding?.textColor || '#020817';
  const textSecondaryColor = config?.branding?.textSecondaryColor || '#6b7280';
  const backgroundColor = config?.branding?.backgroundColor || '#ffffff';
  const borderColor = config?.branding?.borderColor || '#e5e7eb';
  const orderSummaryBgColor = config?.orderSummarySettings?.backgroundColor || '#f1f5f9';
  const offerCardBgColor = config?.offerSettings?.cardBackgroundColor || '#f1f5f9';

  const getOpacity = (opacity?: number) => {
    if (!opacity || opacity > 100 || opacity < 0) {
      return 100;
    }
    return opacity;
  }


  const orderSummaryOpacity = getOpacity(config?.orderSummarySettings?.opacity);
  const offerCardOpacity = getOpacity(config?.offerSettings?.cardOpacity);

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: `
      :root {
        --primary-color: ${primaryColor};
        --ring: ${primaryColor};
        --primary: ${primaryColor};
        --text-color: ${textColor};
        --text-secondary-color: ${textSecondaryColor};
        --background-color: ${backgroundColor};
        --border-color: ${borderColor};
        --font-sans: ${fontFamily}, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";
        --checkout-order-summary-bg: ${getColorOpacityFromHex(orderSummaryBgColor, orderSummaryOpacity)};
        --background-color-hover: ${getColorOpacityFromHex(orderSummaryBgColor, 75)};
        --offer-card-bg: ${getColorOpacityFromHex(offerCardBgColor, offerCardOpacity)};
      }
      body {
        font-family: var(--font-sans);
        color: var(--text-color);
        background-color: var(--background-color);
      }
      .focus-primary:focus {
        border-color: ${primaryColor} !important;
        border-width: 2px !important;
        box-shadow: none !important;
        outline: none !important;
      }
      .focus-visible-primary:focus-visible {
        border-color: ${primaryColor} !important;
        border-width: 2px !important;
        box-shadow: none !important;
        outline: none !important;
      }
      .border-primary-custom {
        border-color: ${primaryColor} !important;
        border-style: solid !important;
        border-width: 2px !important;
      }

      .border-primary-light-custom {
        border-color: ${primaryColor}80 !important;
        border-style: solid !important;
      }
      * {
        border-style: solid;
      }
      [data-slot='radio-group-item'][data-state='checked'] {
        background-color: ${primaryColor};
        border-color: ${primaryColor};
      }
      [data-slot='radio-group-indicator'] svg {
        color: white !important;
        fill: white !important;
      }
      /* Shipping method selection styling */
      label:has([data-slot='radio-group-item'][data-state='checked']) {
        border-color: ${primaryColor} !important;
        background-color: ${getColorOpacityFromCSSVar('primary-color', 8)} !important;
      }
      label:has([data-slot='radio-group-item'][data-state='checked']):focus,
      label:has([data-slot='radio-group-item'][data-state='checked']):focus-visible,
      label:has([data-slot='radio-group-item'][data-state='checked']):focus-within {
        border-color: ${primaryColor} !important;
        box-shadow: 0 0 0 3px ${primaryColor}40 !important; /* 40 = 25% opacity */
        outline: none !important;
      }
      /* Checkbox styling */
      button[role="checkbox"][data-state="checked"] {
        background-color: ${primaryColor} !important;
        border-color: ${primaryColor} !important;
      }
      button[role="checkbox"][data-state="checked"] svg {
        color: white !important;
        stroke: white !important;
      }
      /* Override Tailwind primary classes */
      .border-primary {
        border-color: ${primaryColor} !important;
      }
      .text-primary {
        color: ${primaryColor} !important;
      }
      /* Subtle scrollbar for sticky sidebar when content overflows */
      .sticky-sidebar-scroll {
        scrollbar-width: thin;
        scrollbar-color: rgba(156, 163, 175, 0.3) transparent;
      }
      .sticky-sidebar-scroll::-webkit-scrollbar {
        width: 4px;
      }
      .sticky-sidebar-scroll::-webkit-scrollbar-track {
        background: transparent;
      }
      .sticky-sidebar-scroll::-webkit-scrollbar-thumb {
        background-color: rgba(156, 163, 175, 0.3);
        border-radius: 2px;
      }
      .sticky-sidebar-scroll::-webkit-scrollbar-thumb:hover {
        background-color: rgba(156, 163, 175, 0.5);
      }
      /* Hide scrollbar when not needed */
      .sticky-sidebar-scroll::-webkit-scrollbar-thumb {
        background-color: transparent;
        transition: background-color 0.2s ease;
      }
      .sticky-sidebar-scroll:hover::-webkit-scrollbar-thumb {
        background-color: rgba(156, 163, 175, 0.3);
      }
      .sticky-sidebar-scroll:hover::-webkit-scrollbar-thumb:hover {
        background-color: rgba(156, 163, 175, 0.5);
      }
    `,
      }}
    />
  );
}
